// Generate dynamic assessment analysis based on scores and risk levels

export interface AssessmentAnalysis {
  overallSummary: string
  categoryInsights: Record<string, string>
  recommendations: string[]
}

const CATEGORY_NAMES: Record<string, string> = {
  overall: 'Overall Assessment',
  sexual_desire: 'Sexual Desire',
  coercive_control: 'Coercive Control',
  financial_coercion: 'Financial Coercion',
  gender_role_hierarchy: 'Gender Role Hierarchy',
  substance_use_acute_risk: 'Substance Use & Acute Risk',
  role_challenge_supported_abuse: 'Role Challenge & Supported Abuse',
  communication_anger_dysregulation: 'Communication & Anger Dysregulation',
  emotional_instability_dysfunction: 'Emotional Instability & Dysfunction',
}

const getRiskAnalysis = (category: string, score: number, riskLevel: string): string => {
  const categoryName = CATEGORY_NAMES[category] || category.replace(/_/g, ' ')
  const normalizedRisk = riskLevel.toLowerCase()

  // Category-specific analysis
  const categoryAnalysis: Record<string, Record<string, string>> = {
    overall: {
      low: `Your overall assessment score of ${score} indicates a healthy relationship dynamic with strong foundations. You and your partner demonstrate positive communication patterns and mutual respect. Continue nurturing these strengths while remaining attentive to areas that could benefit from growth.`,
      moderate: `Your overall assessment score of ${score} suggests that while your relationship has many positive aspects, there are some areas that would benefit from attention and improvement. This is common in relationships and indicates opportunities for growth together. Focus on open communication and consider seeking guidance in specific areas highlighted in your results.`,
      high: `Your overall assessment score of ${score} indicates significant concerns that require immediate attention. This score suggests patterns that could be harmful to your wellbeing or your partner's. It's important to take these results seriously and consider reaching out to a professional counselor or therapist who can provide personalized support and guidance.`,
    },
    coercive_control: {
      low: `Your score of ${score} in Coercive Control indicates a healthy balance of independence and togetherness in your relationship. Both partners appear to respect each other's autonomy, decisions, and personal boundaries. This is a strong foundation for a lasting partnership.`,
      moderate: `Your score of ${score} in Coercive Control suggests some concerning patterns where one partner may be exerting undue influence over the other's decisions or freedom. While not yet severe, these behaviors can escalate if left unaddressed. Consider discussing boundaries and autonomy with your partner or a counselor.`,
      high: `Your score of ${score} in Coercive Control indicates serious patterns of one partner controlling, monitoring, or restricting the other's freedom. This is a significant red flag that requires immediate attention. Please consider reaching out to a domestic violence hotline or counselor for support and safety planning.`,
    },
    financial_coercion: {
      low: `Your score of ${score} in Financial Coercion shows healthy financial dynamics where both partners have appropriate access to resources and participate in financial decisions. Financial transparency and equality are present in your relationship.`,
      moderate: `Your score of ${score} in Financial Coercion indicates some concerning patterns around money and financial control. One partner may be limiting the other's access to finances or making unilateral financial decisions. Address these concerns through open communication about financial partnership and equality.`,
      high: `Your score of ${score} in Financial Coercion reveals serious financial control issues where one partner is being restricted from financial resources or independence. This is a form of abuse that can trap individuals in unhealthy relationships. Seek professional guidance to address these patterns and explore options for financial independence.`,
    },
    sexual_desire: {
      low: `Your score of ${score} in Sexual Desire indicates a healthy, consensual intimate relationship where both partners' boundaries and desires are respected. Communication about intimacy appears to be open and comfortable.`,
      moderate: `Your score of ${score} in Sexual Desire suggests some challenges around intimacy, consent, or communication about sexual needs. There may be pressure, discomfort, or mismatched expectations. Open, honest conversations about intimacy and boundaries can help improve this area.`,
      high: `Your score of ${score} in Sexual Desire indicates concerning patterns around sexual coercion, pressure, or violation of consent. Sexual intimacy should always be mutual and freely given. If you're experiencing pressure or coercion in intimate situations, this is serious and warrants professional support.`,
    },
    communication_anger_dysregulation: {
      low: `Your score of ${score} in Communication & Anger Dysregulation shows that you and your partner handle conflicts constructively and manage emotions effectively. Disagreements are resolved with respect and understanding.`,
      moderate: `Your score of ${score} in Communication & Anger Dysregulation indicates some difficulties in managing conflict or expressing anger appropriately. There may be raised voices, hurtful words, or poor communication during disagreements. Learning healthy communication and anger management techniques can significantly improve your relationship.`,
      high: `Your score of ${score} in Communication & Anger Dysregulation reveals serious concerns about how anger is expressed in your relationship. This may include verbal aggression, intimidation, or explosive outbursts. These patterns can be harmful and frightening. Consider seeking professional help to learn healthier communication and emotion regulation strategies.`,
    },
    emotional_instability_dysfunction: {
      low: `Your score of ${score} in Emotional Instability & Dysfunction indicates emotional stability and healthy coping mechanisms within your relationship. Both partners appear to manage stress and emotions effectively.`,
      moderate: `Your score of ${score} in Emotional Instability & Dysfunction suggests some challenges with emotional regulation or stress management that may be affecting your relationship. These patterns can be addressed through individual or couples counseling and learning coping strategies.`,
      high: `Your score of ${score} in Emotional Instability & Dysfunction indicates significant emotional challenges that are impacting your relationship's health. Severe mood swings, emotional volatility, or mental health concerns may be present. Professional mental health support is strongly recommended to address these issues.`,
    },
  }

  // Get specific analysis or generate default
  const analysis = categoryAnalysis[category]?.[normalizedRisk]
  if (analysis) return analysis

  // Default analysis for categories without specific text
  if (normalizedRisk === 'low') {
    return `Your score of ${score} in ${categoryName} indicates positive patterns in this area. Continue maintaining these healthy dynamics while staying aware of potential changes.`
  } else if (normalizedRisk === 'moderate') {
    return `Your score of ${score} in ${categoryName} suggests some areas for improvement. While not critical, addressing these patterns now can prevent escalation and strengthen your relationship.`
  } else {
    return `Your score of ${score} in ${categoryName} indicates concerning patterns that require attention. Consider seeking professional guidance to address these issues effectively.`
  }
}

export const generateAssessmentAnalysis = (
  scores: Record<string, number>,
  riskLevels: Record<string, string>
): AssessmentAnalysis => {
  const overallScore = scores.overall || 0
  const overallRisk = riskLevels.overall || 'low'

  // Generate overall summary
  const overallSummary = getRiskAnalysis('overall', overallScore, overallRisk)

  // Generate category-specific insights
  const categoryInsights: Record<string, string> = {}
  Object.keys(scores).forEach((category) => {
    if (category !== 'overall') {
      const score = scores[category]
      if (score !== undefined) {
        categoryInsights[category] = getRiskAnalysis(
          category,
          score,
          riskLevels[category] || 'low'
        )
      }
    }
  })

  // Generate recommendations based on risk levels
  const recommendations: string[] = []
  const highRiskCategories = Object.keys(riskLevels).filter(
    (key) => riskLevels[key]?.toLowerCase() === 'high' && key !== 'overall'
  )
  const moderateRiskCategories = Object.keys(riskLevels).filter(
    (key) => riskLevels[key]?.toLowerCase() === 'moderate' && key !== 'overall'
  )

  if (highRiskCategories.length > 0) {
    recommendations.push(
      'Reach out to a professional counselor or therapist who specializes in relationship health and safety.'
    )
    recommendations.push(
      'Consider contacting a domestic violence hotline if you feel unsafe (National Hotline: 1-800-799-7233).'
    )
    recommendations.push(
      'Develop a safety plan and identify trusted friends or family members who can support you.'
    )
  }

  if (moderateRiskCategories.length > 0) {
    recommendations.push(
      'Explore couples counseling to work through challenges together in a safe, guided environment.'
    )
    recommendations.push(
      'Begin conversations with your partner about the areas that need attention, using "I feel" statements.'
    )
  }

  if (highRiskCategories.length === 0 && moderateRiskCategories.length === 0) {
    recommendations.push(
      'Continue nurturing your relationship through regular communication and quality time together.'
    )
    recommendations.push(
      'Consider preventive couples enrichment programs to strengthen your bond further.'
    )
    recommendations.push(
      'Stay engaged with the learning modules to continue growing together.'
    )
  }

  return {
    overallSummary,
    categoryInsights,
    recommendations,
  }
}

export const getCategoryName = (category: string): string => {
  return CATEGORY_NAMES[category] || category.replace(/_/g, ' ')
}
